import { Modal, Form, Input, Row, Col, Radio, Cascader, Select, message } from 'antd';
import { debounce, get } from 'lodash-es';
import React, { useState, useEffect } from 'react';
import IconClose from '@/assets/images/icons/icon-close.react.svg?react';
import InfoCircleFill from '@/assets/images/icons/info-circle-fill.react.svg?react';
import { useAppConfig } from '@/config/context';
import { userImplement } from '@/config/hooks/user';
import { BusiCode } from '@/infrastructure/axios/code';
import { getAppIcon, Platform } from '@/infrastructure/utils/app';
import { hintIconText } from '@/presentation/components/hint-text/hintText';
import { APPLY_MESSAGE } from '@/presentation/config/message';
import { OrgTypeName } from '@/presentation/pages/product/info';
import { IApplyProductReq, OrgType, AppForm, IProductDetailInfo } from '@/proto/manage';
import { userService } from '@/service/pro-user';
import { productService } from '@/service/product';
import styles from './style.module.scss';

export interface AddAppReq {
  rdmProductId: string;
  pid: number;
  name: string;
  type: number;
  logoUrl: string;
  ownerId: string;
  desc: string;
  appFrom: number;
  admins: string[];
  appForm: number;
}

interface FormFieldType {
  platformId: number;
  name: string;
  company: string;
  type: number[];
  admins: {value: string, label: string}[];
  description: string;
  organizationType: number;
  appIdOnBuglyqq: string;
}

interface SelectAppType {
  value?: (number | string)[];
  onChange?: (value: (number | string)[]) => void
}

export enum Id {
  MIG = 954,
  SNG = 955,
  OMG = 957,
  IEG = 956,
  CDG = 953,
  TEG = 958,
  CIO = 9832,
  EI = 1000009,
  WXG = 14129,
  S3 = 2234,
  S1 = 78,
  S2 = 2233,
  WeBank = 88888,
}

export const BG_ID = {
  [Id.MIG]: 1, // MIG
  [Id.SNG]: 4, // SNG
  [Id.OMG]: 6, // OMG
  [Id.IEG]: 7, // IEG
  [Id.CDG]: 9, // CDG
  [Id.TEG]: 11, // TEG
  [Id.CIO]: 13, // 公司级组织
  [Id.EI]: 12, // 企业互联
  [Id.WXG]: 14, // WXG
  [Id.S3]: 15, // S3
  [Id.S1]: 16, // S1
  [Id.S2]: 17, // S2
  [Id.WeBank]: 18, // WeBank
};

export const ALLTYPE_OPTIONS = [
  { label: '软件', value: 1 },
  { label: '游戏', value: 2 },
  { label: '办公商务', value: 3 },
  { label: '聊天通讯', value: 4 },
  { label: '系统输入', value: 5 },
  { label: '手机美化', value: 6 },
  { label: '阅读学习', value: 7 },
  { label: '出行地图', value: 8 },
  { label: '影音视听', value: 9 },
  { label: '儿童亲子', value: 10 },
  { label: '拍摄美化', value: 11 },
  { label: '购物时尚', value: 12 },
  { label: '生活娱乐', value: 13 },
  { label: '实用工具', value: 14 },
  { label: '动作冒险', value: 15 },
  { label: '飞行射击', value: 16 },
  { label: '角色扮演', value: 17 },
  { label: '经营策略', value: 18 },
  { label: '体育竞速', value: 19 },
  { label: '网络游戏', value: 20 },
  { label: '益智游戏', value: 21 },
  { label: '休闲娱乐', value: 22 },
];

export const PLATFORM_OPTIONS = [
  { label: 'Android', value: Platform.Android },
  { label: 'iOS', value: Platform.IOS },
  { label: 'Flutter', value: Platform.Flutter },
  { label: 'Harmony', value: Platform.HarmonyOS },
  { label: '小程序', value: Platform.MiniProgram },
  // { label: 'Mac', value: Platform.Mac },
  { label: 'Windows', value: Platform.Windows },
];

export const ORG_TYPE = [
  { label: '公司', value: OrgType.COMPANY },
  { label: '组织', value: OrgType.ORGANIZATION },
  { label: '个人', value: OrgType.INDIVIDUAL },
];

const NOT_ASSOCIATED = '-1';

export const ILLEGAL_REG = /^[\u4e00-\u9fa5a-zA-Z0-9=+/@#_-]+$/;
export const BUNDLE_ID_REG = /^[a-zA-Z0-9\\.]+$/;
export const DESC_REG = /^[\u4e00-\u9fa5a-zA-Z0-9.,#:*$\s@_\-\\()]+$/;

export const CreateApp = (props: { visible: boolean, handleCancel: () => void }) => {
  const { visible, handleCancel } = props;
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [adminOptions, setAdminOptions] = useState<{value: string, label: string}[]>([]);
  const [buglyqqProducts, setBuglyqqProducts] = useState<IProductDetailInfo[]>([]);
  const [form] = Form.useForm();
  const config = useAppConfig();

  useEffect(() => {
    if (visible) {
      queryBuglyqqProducts();
      return;
    }
    // 关闭后初始化表单
    form.resetFields();
  }, [visible]);

  const queryBuglyqqProducts = async () => {
    const data = await productService.getBuglyqqProducts({ buglyId: userImplement.getUserId() });
    setBuglyqqProducts(data);
  };
  const handleSubmit = async (values: FormFieldType) => {
    const params: IApplyProductReq = {
      platformId: values.platformId,
      name: values.name,
      orgType: values.organizationType,
      company: values.company,
      type: values.type?.[1],
      admins: values?.admins?.map?.(item => item.value),
      description: values.description,
      appForm: AppForm.APP, // 默认为APP
      appIdOnBuglyqq: values.appIdOnBuglyqq === NOT_ASSOCIATED ? undefined : values.appIdOnBuglyqq,
    };
    const data = await productService.applyProduct(params);
    setConfirmLoading(false);
    if (data.baseRsp?.code !== BusiCode.Ok) {
      if (data.baseRsp?.msg) {
        message.error(data.baseRsp?.msg);
      }
      return;
    }
    handleCancel();
    Modal.info({
      closable: true,
      icon: <InfoCircleFill/>,
      closeIcon: <IconClose/>,
      className: styles.notify,
      title: '通知',
      okText: '我知道了',
      content: APPLY_MESSAGE,
    });
  };

  const handleOk = () => {
    setConfirmLoading(true);
    form.validateFields()
      .then((values: FormFieldType) => {
        // 提交
        handleSubmit(values);
      })
      .catch(() => {
        setConfirmLoading(false);
      });
  };

  const AppType = ({ onChange, value = [] }: SelectAppType) => {
    const options = [
      {
        ...ALLTYPE_OPTIONS[0],
        children: ALLTYPE_OPTIONS.slice(2, 14),
      },
      {
        ...ALLTYPE_OPTIONS[1],
        children: ALLTYPE_OPTIONS.slice(14, 22),
      },
    ];
    const handleAppType = (AppTypeValue: (number | string)[], selectedOptions: Record<string, any>[]) => {
      // 当未选择时点击第一列，默认选中第二列的第一条
      if (AppTypeValue?.length === 1 && value?.[0] !== AppTypeValue[0]) {
        AppTypeValue.push(selectedOptions[0]?.children?.[0]?.value);
        onChange?.(AppTypeValue);
      } else if (AppTypeValue?.length === 1 && AppTypeValue?.[0] === value?.[0] && value?.length === 2) {
        AppTypeValue.push(value[1]);
        onChange?.(AppTypeValue);
      }
      if (AppTypeValue?.length === 2) {
        onChange?.(AppTypeValue);
      }
    };

    return <Cascader changeOnSelect onChange={handleAppType} value={value} options={options} placeholder="请选择" />;
  };

  const adminSearch = debounce(async (value: string) => {
    if (!value) {
      setAdminOptions([]);
      return;
    }

    const idToNick = await  userService.getNickNameByBuglyIDs({ buglyIds: [value] });
    const name = get(idToNick, value);
    setAdminOptions([{ value, label: name ? `${name}（${value}）` : value }]);
  }, 100);

  const platformChange = () => {
    form.setFieldsValue({ appIdOnBuglyqq: NOT_ASSOCIATED });
  };

  const filterOption = (inputValue: string, opt: any) => opt?.label?.toLowerCase()?.indexOf(inputValue?.toLocaleLowerCase() ?? '') !== -1;

  return (
    <Modal
      title="申请创建产品"
      width={850}
      maskClosable={false}
      visible={visible}
      onOk={handleOk}
      cancelButtonProps={{ className: styles.cancelButton }}
      okButtonProps={{ className: styles.okButton }}
      closeIcon={<IconClose/>}
      confirmLoading={confirmLoading}
      onCancel={() => handleCancel()}
      className={styles.createModal}
    >
      <Form<FormFieldType> form={form} layout={'vertical'}>
        <Form.Item
          label="接入平台"
          name="platformId"
          initialValue={PLATFORM_OPTIONS[0].value}
          rules={[{ required: true, message: '请选择接入平台!' }]}
        >
          <Radio.Group className={styles.radio} onChange={platformChange}>
            {PLATFORM_OPTIONS.map(opt => (
              <Radio value={opt.value}>
                <div className={styles.appItem}>
                  <span>{getAppIcon(`${opt.value}`, opt.value === Platform.Flutter)}</span>
                  <span>{opt.label}</span>
                </div>
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>
        <Row gutter={12}>
          <Col span={24}>
            <Form.Item
              label="产品名称"
              name="name"
              rules={[
                { required: true, message: '请输入产品名称!' },
                { max: 20, message: '字数不能超过20个字符!', type: 'string' },
                { pattern: ILLEGAL_REG, message: '产品名称含有非法字符!!' },
              ]}
            >
              <Input placeholder={'请输入仅包含中文字符、英文字母、数字以及特殊字符（=、+、/、@、#、_、-）的字符串'} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={24}>
            <Form.Item
              label="组织类型"
              name="organizationType"
              rules={[{ required: true, message: '请选择组织类型!' }]}
            >
              <Radio.Group
                className={styles.radio}
                options={ORG_TYPE}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item noStyle shouldUpdate={
          (
            prevValues: FormFieldType,
            curValues: FormFieldType,
          ) => prevValues.organizationType !== curValues.organizationType
        }>
          {({ getFieldValue }) => {
            const userType = getFieldValue('organizationType');
            if (![OrgType.ORGANIZATION, OrgType.COMPANY].includes(userType)) {
              return;
            }
            return <Row gutter={12}>
              <Col span={24}>
                <Form.Item
                  preserve={false}
                  name="company"
                  rules={[{ required: true, message: `请输入${OrgTypeName[userType]}名称!` }]}
                >
                  <Input placeholder={`请输入${OrgTypeName[userType]}名称`} />
                </Form.Item>
              </Col>
            </Row>;
          }}
        </Form.Item>
        <Row gutter={12}>
          <Col span={24}>
            <Form.Item
              label="产品类型"
              name="type"
              rules={[{ required: true, message: '请选择产品类型!' }]}
            >
              <AppType />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={24}>
            <Form.Item
              label="管理员"
              name="admins"
              initialValue={[{ value: userImplement.getUserId(), label: `${decodeURIComponent(userImplement.getUserName())}（${userImplement.getUserId()}）` }]}
              rules={[
                { required: true, message: '请输入管理员用户 ID，至少需要一个管理员' },
                { message: '最多只允许设置10个管理员!', max: 10, type: 'array' },
              ]}
            >
              <Select
                onChange={() => setAdminOptions([])}
                options={adminOptions}
                onSearch={adminSearch}
                labelInValue={true}
                optionLabelProp={'label'}
                mode={'tags'}
                showSearch={true}
                suffixIcon={<></>}
                tokenSeparators={[';', '；']}
                placeholder="请输入管理员用户 ID"
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={24}>
            <Form.Item
              label="产品描述"
              name="description"
            >
              <Input
                placeholder={'请输入'}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={24}>
            <Form.Item shouldUpdate>
              {({ getFieldValue }) => {
                const platformId = getFieldValue('platformId');
                const options = buglyqqProducts.filter(item => item.platformId === platformId)
                  .map(item => ({ label: `${item.productName}（${item.productId}）`, value: item.productId }));
                options.unshift({ label: '不关联', value: NOT_ASSOCIATED });
                return <Form.Item
                  initialValue={NOT_ASSOCIATED}
                  label={hintIconText('关联Bugly QQ上的相同产品', config.buglyQQMessage)}
                  name="appIdOnBuglyqq"
                >
                  <Select
                    options={options}
                    placeholder={'请选择Bugly QQ上的相同产品'}
                    showSearch
                    filterOption={filterOption}
                  />
                </Form.Item>;
              }}

            </Form.Item>

          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
